import { Button, ButtonGroup, Classes, H3 } from "@blueprintjs/core";
import React from "react";

import Class from "core/util/Class";
import IEntity from "core/util/IEntity";
import ServiceRegistry from "core/util/ServiceRegistry";
import { IIntentLabel } from "./label/ILabel";
import Label from "./label/Label";

/**
 * WORK IN PROGRESS, BREAKING CHANGES MIGHT COME
 *
 * @author chills
 *
 * use like:
 *
 * @Entity({one: "article_one"})
 * class Article {
 *
 * 	@Field()
 * 	public title: string;
 *
 * }
 *
 * <EntityEditWrapper
 * 		entity={Article}
 * 		canEdit={(article) => }
 * 		params={{id: props.pageParams.articleId}}
 *
 * 		onAfterLoad={
 * 			(response) => {
 * 				if (response.uiSchema) {
 * 					response.uiSchema = JSON.parse(response.uiSchema)
 * 				}
 * 				return response;
 * 			}
 * 		}
 * >
 * 	{({ data, editing, onChange }) => {
 *
 * 		return (
 * 			<div>
 * 				{
 * 					editing ?
 * 					(
 * 						<TextWidget
 * 							value={data.title}
 * 							placeholder="Enter a title"
 * 							onChange={(title) => onChange({...data, title})}
 * 						/>
 * 					) : (
 * 						<H1>{data.title}</h1>
 * 					)
 * 				}
 * 			</div>
 * 		);
 * 	}}
 * </EntityEditWrapper>
 */

/**
 * @argument E - (Entity) the entity type
 * @argument S - (State Data) the type saved into state after optional transformation
 * @argument A - (Action Params) the action params type, if different than the entity itself
 */
interface IEntityEditWrapperProps<E, S = E, A = E> {

	/**
	 * TypeGraph decorated data class to load
	 * must have the Entity decorator with a name for a "one" query supplied.
	 */
	entity: Class<E>;

	/**
	 * params for loading teh entity
	 */
	params: object;

	children: (args: IEntityEditWrapperArgs<S>) => React.ReactNode;

	canEdit: boolean | ((o: S) => boolean);

	title?: string | JSX.Element;

	showButtonGroup?: boolean;

	/**
	 * do optional side effects and transformations
	 */
	onAfterLoad?: (o: E) => S;

	/**
	 * do optional side effects and transformations before saving
	 * @param o - the data in state
	 */
	onBeforeSave?: (o: S) => A;

	/**
	 * do things after saving
	 */
	onAfterSave?: (err: null | any, o: E) => void;
}

/**
 * @argument S - (State Data) data type
 */
interface IEntityEditWrapperState<S> {
	readonly changes: number;
	readonly data: null | S;
	readonly loading: boolean;
	readonly saving: boolean;
	readonly editing: boolean;
	readonly feedback: IIntentLabel[];
}

interface IEntityEditWrapperArgs<S> extends IEntityEditWrapperState<S> {
	onChange: (o: S) => void;
	toggleEditing: () => void;
}

export class EntityEditWrapper<E extends IEntity, S extends IEntity = E, A extends IEntity = E> extends React.Component<IEntityEditWrapperProps<E, S, A>, IEntityEditWrapperState<S>> {

	public static ofType<E extends IEntity, S extends IEntity = E, A extends IEntity = E>() {
		return EntityEditWrapper as new (props: IEntityEditWrapperProps<E, S, A>) => EntityEditWrapper<E, S, A>;
	}

	public constructor(props: IEntityEditWrapperProps<E, S, A>) {
		super(props);
		this.state = {
			changes: 0,
			data: null,
			loading: false,
			saving: false,
			editing: false,
			feedback: []
		};
	}

	public render() {

		const { canEdit, children, showButtonGroup=true } = this.props;

		const content = children({
			...this.state,
			onChange: this.onChange,
			toggleEditing: this.toggleEditing
		});

		if (
			!!this.state.data && (
				(typeof canEdit === 'function' && canEdit(this.state.data)) ||
				(typeof canEdit === 'boolean' && canEdit)
			)
		) {

			return (
				<div>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							backgroundColor: '#f5f8fa',
							padding: "15px"
						}}
					>
						<div>
							{
								this.props.title && (
									<H3 style={{margin: 0}}>
										{this.props.title}
									</H3>
								)
							}
						</div>
						{
							showButtonGroup ? (
								<ButtonGroup>
							<Button
								text={
									this.state.editing ?
										<Label k="preview" v="View" /> :
										<Label k="edit" v="Edit" />
								}
								icon={this.state.editing ? "eye-open" : "edit"}
								onClick={this.toggleEditing}
								active={this.state.editing}
							/>
							{
								this.state.changes > 0 && (
									<Button
										intent="primary"
										icon="saved"
										text={<Label k="save" v="Save" />}
										loading={this.state.saving}
										onClick={this.save}
									/>
								)
							}
						</ButtonGroup>
							) : ''
						}
					</div>
					{content}
				</div>
			);
		}

		return content;
	}

	public componentDidMount() {
		this.loadData();
	}

	private toggleEditing = () => this.setState({editing: !this.state.editing});

	private onChange = (o: S) => this.setState({data: o, changes: this.state.changes + 1});

	private loadData = () => {

		this.setState({loading: true}, () => {

			ServiceRegistry
				.getQueryService()
				.findOne(this.props.entity, this.props.params || {})
				.then((res: any) => {

					const o = res.data.entity || null;

					this.setState({
						saving: false,
						loading: false,
						data: (
							typeof this.props.onAfterLoad === 'function' ?
								this.props.onAfterLoad(o) :
								o
						) as S,
					});
				})
				.catch((err) => {

					console.error(err);

					this.setState({
						loading: false,
						data: null
					});
				});
		});
	}

	private save = () => {
		this.setState({saving: true}, () => {
			const { entity, onBeforeSave } = this.props;

			const actionParams = (typeof onBeforeSave === 'function' ? onBeforeSave(this.state.data) : this.state.data) as A;

			ServiceRegistry
				.getMutationService()
				.update(actionParams, entity as any)
				.then((res) => {

					const o = res.entity || null;

					this.setState({
						saving: false,
						editing: false,
						loading: false,
						changes: 0,
						data: (
							typeof this.props.onAfterLoad === 'function' ?
								this.props.onAfterLoad(o as any) :
								o
						) as S
					}, () => {
						if (typeof this.props.onAfterSave === 'function') {
							this.props.onAfterSave(null, this.state.data as any);
						}
					});
				})
				.catch((err) => {

					console.error(err);

					this.setState({
						saving: false
					});
				});
		});
	}
}



// WEBPACK FOOTER //
// ./src/core/components/EntityEditWrapper.tsx